"use client";

import { useState, useMemo } from "react";
import {
    Dialog,
    DialogContent,
    DialogTrigger,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Plus, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useQueryClient } from "@tanstack/react-query";
import { post, fetchData } from "@/utils/api-handler";
import { useProtectedMutation } from "@/hooks/use-protected-mutation";
import { useProtectedQuery } from "@/hooks/use-protected-query";
import { toast } from "sonner";
import { FormField } from "@/components/shared/form-fields";
import { Loader } from "@/components/ui/loader";
import { VisuallyHidden } from "@/components/ui/visually-hidden";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from "@/components/ui/card";
import { TEXTAREA_MIN_HEIGHT } from "@/utils/global";
import { CommandAgentSharingCard } from "@/components/command-team/command-agent/cards/command-agent-sharing-card";

interface RolesListResponse {
    items?: Array<{ id?: string; title?: string }>;
}

type CreateCommandAgentPayload = {
    name: string;
    description: string | null;
    role: string;
    persona: string;
    restrictions: string | null;
    visibility: AppIntegrationVisibility;
    shared_roles?: string[];
};

interface CreateCommandAgentProps {
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}

export function CreateCommandAgent({ open: externalOpen, onOpenChange: externalOnOpenChange }: CreateCommandAgentProps = {}) {
    const queryClient = useQueryClient();
    const [internalOpen, setInternalOpen] = useState(false);
    const isControlled = externalOpen !== undefined;
    const isDialogOpen = isControlled ? externalOpen : internalOpen;
    const setIsDialogOpen = isControlled ? (externalOnOpenChange || (() => {})) : setInternalOpen;
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        role: "",
        persona: "",
        restrictions: "",
        visibility: "PERSONAL" as AppIntegrationVisibility,
        shared_roles: [] as string[],
    });

    const [errors, setErrors] = useState({
        name: "",
        description: "",
        role: "",
        persona: "",
        restrictions: "",
        visibility: "",
        shared_roles: "",
    });

    const { data: rolesQueryData, isLoading: rolesLoading, hasPermission: canListRoles } =
        useProtectedQuery<RolesListResponse | Array<{ id?: string; title?: string }>>({
            queryKey: ["command-agent-role-options"],
            requiredPermissions: ["LIST_ROLES"],
            queryFn: async () => {
                const params = new URLSearchParams({ page: "1", limit: "500" });
                return fetchData(`/api/rbac/roles?${params.toString()}`);
            },
            enabled: isDialogOpen,
        });

    const roleOptions = useMemo(() => {
        const raw = rolesQueryData;
        const items = Array.isArray(raw) ? raw : raw?.items ?? [];
        return items
            .filter((r): r is { id: string; title?: string } => Boolean(r.id))
            .map((r) => ({ label: r.title?.trim() || r.id, value: r.id }));
    }, [rolesQueryData]);

    const createAgentMutation = useProtectedMutation({
        mutationFn: (data: CreateCommandAgentPayload) => {
            return post("/api/command-team/agents", data);
        },
        requiredPermissions: ["CREATE_COMMAND_AGENT"],
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["command-agents"] });
            queryClient.invalidateQueries({ queryKey: ["command-agents-sidebar"] });
            queryClient.invalidateQueries({ queryKey: ["trigger-route-configuration"] });
            toast.success("Command Agent created successfully", {
                description: "Command Agent created successfully",
            });
            handleCancel();
        },
        onError: (error: any) => {
            const newErrors = {
                name: "",
                description: "",
                role: "",
                persona: "",
                restrictions: "",
                visibility: "",
                shared_roles: "",
            };

            if (error.response?.data?.detail) {
                const validationErrors = error.response.data.detail;

                validationErrors.forEach((err: any) => {
                    const field = err.loc[1];
                    const message = err.msg;

                    switch (field) {
                        case "name":
                            newErrors.name = message;
                            break;
                        case "description":
                            newErrors.description = message;
                            break;
                        case "role":
                            newErrors.role = message;
                            break;
                        case "persona":
                            newErrors.persona = message;
                            break;
                        case "restrictions":
                            newErrors.restrictions = message;
                            break;
                        case "visibility":
                            newErrors.visibility = message;
                            break;
                        case "shared_roles":
                            newErrors.shared_roles = message;
                            break;
                    }
                });

                setErrors(newErrors);
            } else {
                toast.error("Failed to create command agent", {
                    description: error.response?.data?.message || "An unexpected error occurred",
                });
            }
        },
    });

    const handleInputChange = (field: string, value: string) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));

        // Clear error when user starts typing
        if (errors[field as keyof typeof errors]) {
            setErrors((prev) => ({
                ...prev,
                [field]: "",
            }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrors({
            name: "",
            description: "",
            role: "",
            persona: "",
            restrictions: "",
            visibility: "",
            shared_roles: "",
        });

        const payload: CreateCommandAgentPayload = {
            name: formData.name,
            description: formData.description.trim() === "" ? null : formData.description,
            role: formData.role,
            persona: formData.persona,
            restrictions: formData.restrictions.trim() === "" ? null : formData.restrictions,
            visibility: formData.visibility,
        };
        if (formData.visibility === "TEAM" && formData.shared_roles.length > 0) {
            payload.shared_roles = formData.shared_roles;
        }

        createAgentMutation.mutate(payload);
    };

    const handleCancel = () => {
        setFormData({
            name: "",
            description: "",
            role: "",
            persona: "",
            restrictions: "",
            visibility: "PERSONAL" as AppIntegrationVisibility,
            shared_roles: [],
        });
        setErrors({
            name: "",
            description: "",
            role: "",
            persona: "",
            restrictions: "",
            visibility: "",
            shared_roles: "",
        });
        setIsDialogOpen(false);
    };

    return (
        <Dialog open={isDialogOpen} onOpenChange={(open) => { if (open) setIsDialogOpen(true); }}>
            {!isControlled && (
                <DialogTrigger asChild>
                    <Button size="sm" className="bg-primary hover:bg-primary/90">
                        <Plus className="h-4 w-4" />
                        <span className="hidden sm:inline">Create Agent</span>
                        <span className="sm:hidden">New</span>
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent
                className="w-[calc(100%-1rem)] sm:w-full p-0 sm:max-w-[830px] [&>button:last-child]:hidden max-h-[92dvh] sm:max-h-[85vh] overflow-y-auto"
                onInteractOutside={(e) => e.preventDefault()}
                onPointerDownOutside={(e) => e.preventDefault()}
            >
                <DialogHeader>
                    <VisuallyHidden>
                        <DialogTitle>Create Command Agent</DialogTitle>
                    </VisuallyHidden>
                </DialogHeader>
                <div className="sticky top-0 z-50 bg-background border-b px-4 sm:px-6 py-3 sm:py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
                        <div>
                            <h2 className="text-base sm:text-xl font-semibold leading-tight">Create New Command Agent</h2>
                            <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                                Configure your command agent settings.
                            </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <Button
                                variant="outline"
                                onClick={handleCancel}
                                className="flex-1 sm:flex-none"
                            >
                                Cancel
                            </Button>
                            <Button
                                className="bg-primary hover:bg-primary/90 flex-1 sm:flex-none"
                                onClick={handleSubmit}
                                disabled={createAgentMutation.isPending}
                            >
                                {createAgentMutation.isPending ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        <span>Creating...</span>
                                    </>
                                ) : (
                                    <>
                                        <Plus className="w-4 h-4 mr-1" />
                                        <span>Create Agent</span>
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                    {createAgentMutation.isPending && <Loader />}
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="p-3 space-y-3">
                        <Card className="shadow-sm border-border/40">
                            <CardHeader className="pb-2 px-4 sm:px-6">
                                <CardTitle className="text-base font-medium">
                                    General
                                </CardTitle>
                                <CardDescription>
                                    Basic agent configuration
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="px-4 sm:px-6 space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <FormField
                                        label="Agent Name"
                                        error={errors.name}
                                        description="Enter a unique name for your command agent"
                                    >
                                        <Input
                                            value={formData.name}
                                            onChange={(e) => handleInputChange("name", e.target.value)}
                                            placeholder="e.g., Customer Support Agent"
                                            className={`border-border/40 ${errors.name ? "border-destructive" : ""}`}
                                        />
                                    </FormField>

                                    <FormField
                                        label="Role"
                                        error={errors.role}
                                        description="Specify the role of this agent"
                                    >
                                        <Input
                                            value={formData.role}
                                            onChange={(e) => handleInputChange("role", e.target.value)}
                                            placeholder="e.g., Customer Support Specialist"
                                            className={`border-border/40 ${errors.role ? "border-destructive" : ""}`}
                                        />
                                    </FormField>
                                </div>

                                <FormField
                                    label="Description"
                                    error={errors.description}
                                    description="Brief description of the agent's purpose"
                                >
                                    <Textarea
                                        value={formData.description}
                                        onChange={(e) => handleInputChange("description", e.target.value)}
                                        placeholder="Command agent for customer support tasks."
                                        className={`${TEXTAREA_MIN_HEIGHT} ${errors.description ? "border-destructive" : ""}`}
                                    />
                                </FormField>
                            </CardContent>
                        </Card>

                        <CommandAgentSharingCard
                            idPrefix="create-command-agent"
                            visibility={formData.visibility}
                            sharedRoles={formData.shared_roles}
                            canListRoles={canListRoles}
                            rolesLoading={rolesLoading}
                            roleOptions={roleOptions}
                            errors={{
                                visibility: errors.visibility,
                                shared_roles: errors.shared_roles,
                            }}
                            onVisibilityChange={(visibility) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    visibility,
                                    shared_roles: visibility === "TEAM" ? prev.shared_roles : [],
                                }));
                                setErrors((prev) => ({
                                    ...prev,
                                    visibility: "",
                                    shared_roles: "",
                                }));
                            }}
                            onSharedRolesChange={(value) => {
                                setFormData((prev) => ({ ...prev, shared_roles: value }));
                                setErrors((prev) => ({ ...prev, shared_roles: "" }));
                            }}
                        />

                        <Card className="shadow-sm border-border/40">
                            <CardHeader className="pb-2 px-4 sm:px-6">
                                <CardTitle className="text-base font-medium">
                                    Behavior
                                </CardTitle>
                                <CardDescription>
                                    Define the agent&apos;s persona and restrictions
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="px-4 sm:px-6 space-y-6">
                                <FormField
                                    label="Persona"
                                    error={errors.persona}
                                    description="Define the agent's personality and expertise"
                                >
                                    <Textarea
                                        value={formData.persona}
                                        onChange={(e) => handleInputChange("persona", e.target.value)}
                                        placeholder="An expert in handling customer support queries and providing helpful responses."
                                        className={`${TEXTAREA_MIN_HEIGHT} ${errors.persona ? "border-destructive" : ""}`}
                                    />
                                </FormField>

                                <FormField
                                    label="Restrictions"
                                    error={errors.restrictions}
                                    description="Specify what the agent should not do or avoid"
                                >
                                    <Textarea
                                        value={formData.restrictions}
                                        onChange={(e) => handleInputChange("restrictions", e.target.value)}
                                        placeholder="Do not provide financial advice or make promises."
                                        className={`${TEXTAREA_MIN_HEIGHT} ${errors.restrictions ? "border-destructive" : ""}`}
                                    />
                                </FormField>
                            </CardContent>
                        </Card>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}

